import { useEffect, useState } from "react";
import toast from "react-hot-toast";
import { Loader2, Plus, Gift } from "lucide-react";
import Sidebar from "../components/Sidebar";
import Topbar from "../components/Topbar";
import SuperAdminOnlyNote from "../components/SuperAdminOnlyNote";
import api from "../services/api";
import { isSuperAdmin } from "../utils/role";
import { formatLagosDate } from "../utils/tripLog";

// All promotion data comes from the backend (the "bucket"). Budgets/amounts are
// entered in naira here and sent to the backend in kobo. Nothing is hardcoded.

const naira = (kobo) =>
  "₦" +
  Number((kobo || 0) / 100).toLocaleString("en-NG", {
    maximumFractionDigits: 0,
  });

/**
 * What is actually limiting this promotion, and how far through it is.
 *
 * A promotion is capped by exactly ONE of three things — budget, number of
 * redemptions, or a date range — and the admin form makes you pick. The bar
 * used to assume budget always, so a campaign capped by headcount or duration
 * showed "₦0 of ₦0 used" at 0%: indistinguishable from an unfunded, dead promo.
 *
 * `budgetKobo: 0` is meaningful, not missing — the backend reads it as "no
 * budget cap, capped some other way".
 */
const describeCap = (p) => {
  const spent = p.spentKobo || 0;
  const budget = p.budgetKobo || 0;
  const redeemed = p.redemptionCount || 0;
  const maxRedemptions = p.maxRedemptions || 0;

  const pctOf = (used, total) =>
    total > 0 ? Math.min(Math.round((used / total) * 100), 100) : 0;

  if (budget > 0) {
    return {
      kind: "budget",
      pct: pctOf(spent, budget),
      usedLabel: `${naira(spent)} of ${naira(budget)} used`,
      // Clamped at zero: the bar caps at 100% but this label did not, so an
      // overspend would have read as "-₦500 left".
      leftLabel: `${naira(Math.max(budget - spent, 0))} left`,
      capLabel: `${redeemed} redemption${redeemed === 1 ? "" : "s"}`,
    };
  }

  if (maxRedemptions > 0) {
    return {
      kind: "people",
      pct: pctOf(redeemed, maxRedemptions),
      usedLabel: `${redeemed} of ${maxRedemptions} redemptions used`,
      leftLabel: `${Math.max(maxRedemptions - redeemed, 0)} left`,
      capLabel: "Capped by number of people",
    };
  }

  if (p.endsAt) {
    const start = p.startsAt ? new Date(p.startsAt).getTime() : null;
    const end = new Date(p.endsAt).getTime();
    const now = Date.now();
    const msLeft = end - now;
    const daysLeft = Math.max(Math.ceil(msLeft / 86400000), 0);
    // Without a start date there is no window to measure against, so show
    // elapsed-vs-remaining only when we genuinely know both ends.
    const pct =
      start && end > start
        ? Math.min(Math.max(Math.round(((now - start) / (end - start)) * 100), 0), 100)
        : msLeft <= 0
          ? 100
          : 0;
    return {
      kind: "duration",
      pct,
      usedLabel:
        msLeft <= 0
          ? "Ended"
          : `Runs until ${formatLagosDate(p.endsAt, "en-NG", {
              day: "numeric",
              month: "short",
            })}`,
      leftLabel: msLeft <= 0 ? "0 days left" : `${daysLeft} day${daysLeft === 1 ? "" : "s"} left`,
      capLabel: `${redeemed} redemption${redeemed === 1 ? "" : "s"}`,
    };
  }

  // No cap at all. The backend refuses to run a custom discount in this state
  // (isBoundedCampaign), so say so rather than drawing a meaningless bar.
  return {
    kind: "none",
    pct: 0,
    usedLabel: "No budget, headcount or end date set",
    leftLabel: "Not running",
    capLabel: `${redeemed} redemption${redeemed === 1 ? "" : "s"}`,
  };
};

const TYPE_LABEL = {
  first_ride_free: "First Ride Free",
  waive_service_fee: "Waive Zooma Charge",
  rider_milestone_reward: "Rider Reward",
  custom_discount: "Custom Discount",
};

const emptyForm = {
  type: "first_ride_free",
  title: "",
  description: "",
  terms: "",
  audience: "customer",
  budgetNaira: "",
  rewardNaira: "",
  milestoneRides: 5,
  active: false,
  // Custom-discount (granular) fields
  rideCategory: "both",
  percentOffFare: "",
  percentOffCharge: "",
  // A custom discount is capped by exactly ONE of these — chosen via limitType.
  limitType: "budget", // "budget" | "people" | "duration"
  maxRedemptions: "",
  startsAt: "",
  endsAt: "",
};

const Promotions = () => {
  const [promos, setPromos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [togglingId, setTogglingId] = useState(null);
  const [showCreate, setShowCreate] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const canEdit = isSuperAdmin();

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/v1/admin/promotions");
      setPromos(Array.isArray(data) ? data : data?.data || []);
    } catch (error) {
      toast.error(error?.response?.data?.message || "Failed to load promotions");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const toggleActive = async (promo) => {
    setTogglingId(promo._id);
    try {
      await api.put(`/v1/admin/promotions/${promo._id}/active`, {
        active: !promo.active,
      });
      toast.success(!promo.active ? "Activated" : "Deactivated");
      load();
    } catch (error) {
      toast.error(error?.response?.data?.message || "Could not update");
    } finally {
      setTogglingId(null);
    }
  };

  // Toggle the create form (also resets any in-progress edit).
  const openCreate = () => {
    setEditingId(null);
    setForm(emptyForm);
    setShowCreate((s) => !s);
  };

  // Load an existing promo into the form for editing (kobo → naira for inputs).
  const startEdit = (promo) => {
    setEditingId(promo._id);
    setForm({
      type: promo.type,
      title: promo.title || "",
      description: promo.description || "",
      terms: promo.terms || "",
      audience: promo.audience || "customer",
      budgetNaira: promo.budgetKobo ? String(promo.budgetKobo / 100) : "",
      rewardNaira: promo.rewardKobo ? String(promo.rewardKobo / 100) : "",
      milestoneRides: promo.milestoneRides || 5,
      active: !!promo.active,
      rideCategory: promo.rideCategory || "both",
      percentOffFare: promo.percentOffFare ? String(promo.percentOffFare) : "",
      percentOffCharge: promo.percentOffCharge ? String(promo.percentOffCharge) : "",
      // Infer which single limit this promo uses.
      limitType: promo.maxRedemptions
        ? "people"
        : promo.startsAt || promo.endsAt
          ? "duration"
          : "budget",
      maxRedemptions: promo.maxRedemptions ? String(promo.maxRedemptions) : "",
      startsAt: promo.startsAt ? String(promo.startsAt).slice(0, 10) : "",
      endsAt: promo.endsAt ? String(promo.endsAt).slice(0, 10) : "",
    });
    setShowCreate(true);
    if (typeof window !== "undefined")
      window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const cancelForm = () => {
    setEditingId(null);
    setForm(emptyForm);
    setShowCreate(false);
  };

  // Create a new promo, or update the one being edited. Type/audience are set
  // only on create — an existing promo's type never changes.
  const savePromo = async () => {
    if (!form.title.trim()) {
      toast.error("Title is required");
      return;
    }
    if (form.type === "custom_discount") {
      if (Number(form.percentOffFare || 0) <= 0 && Number(form.percentOffCharge || 0) <= 0) {
        toast.error("Enter at least one % off (fare or charge)");
        return;
      }
      if (form.limitType === "budget" && Number(form.budgetNaira || 0) <= 0) {
        toast.error("Enter a budget");
        return;
      }
      if (form.limitType === "people" && Number(form.maxRedemptions || 0) <= 0) {
        toast.error("Enter the number of people");
        return;
      }
      if (form.limitType === "duration" && (!form.startsAt || !form.endsAt)) {
        toast.error("Enter start and end dates");
        return;
      }
    }
    setSaving(true);
    const isCustom = form.type === "custom_discount";
    const payload = {
      title: form.title,
      description: form.description,
      terms: form.terms,
      rewardKobo: Math.round(Number(form.rewardNaira || 0) * 100),
      milestoneRides: Number(form.milestoneRides || 5),
      active: form.active,
    };
    if (isCustom) {
      payload.rideCategory = form.rideCategory || "both";
      payload.percentOffFare = Number(form.percentOffFare || 0);
      payload.percentOffCharge = Number(form.percentOffCharge || 0);
      // Exactly ONE limit — the others are cleared.
      payload.budgetKobo =
        form.limitType === "budget"
          ? Math.round(Number(form.budgetNaira || 0) * 100)
          : 0;
      payload.maxRedemptions =
        form.limitType === "people" ? Number(form.maxRedemptions || 0) : 0;
      payload.startsAt =
        form.limitType === "duration" ? form.startsAt || undefined : undefined;
      payload.endsAt =
        form.limitType === "duration" ? form.endsAt || undefined : undefined;
    } else {
      payload.budgetKobo = Math.round(Number(form.budgetNaira || 0) * 100);
    }
    try {
      if (editingId) {
        await api.put(`/v1/admin/promotions/${editingId}`, payload);
        toast.success("Promotion updated");
      } else {
        await api.post("/v1/admin/promotions", {
          ...payload,
          type: form.type,
          audience:
            form.type === "rider_milestone_reward" ? "rider" : "customer",
        });
        toast.success("Promotion created");
      }
      setForm(emptyForm);
      setEditingId(null);
      setShowCreate(false);
      load();
    } catch (error) {
      toast.error(error?.response?.data?.message || "Could not save");
    } finally {
      setSaving(false);
    }
  };

  const set = (k) => (e) =>
    setForm((f) => ({
      ...f,
      [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value,
    }));

  return (
    <div className="flex min-h-screen bg-gray-50">
      <Sidebar />
      <div className="flex-1 flex flex-col w-full lg:pl-64">
        <Topbar />
        <main className="flex-1 overflow-y-auto p-6">
          <div className="flex items-start justify-between mb-6 gap-4 flex-wrap">
            <div>
              <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
                Promotions
              </h1>
              <p className="text-sm text-gray-500 mt-1">
                Activate/deactivate promotions and track budget consumption.
                First Ride Free and the rider reward pay out automatically.
              </p>
            </div>
            {canEdit ? (
              <button
                onClick={openCreate}
                className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-black text-white text-sm font-medium"
              >
                <Plus className="w-4 h-4" /> New promotion
              </button>
            ) : (
              <SuperAdminOnlyNote />
            )}
          </div>

          {/* Create form */}
          {showCreate && canEdit && (
            <div className="bg-white dark:bg-black rounded-xl shadow-sm p-5 mb-6 max-w-2xl">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <label className="text-sm">
                  <span className="text-gray-500">
                    Type <span className="text-red-500">*</span>{" "}
                    {editingId && "(can't change)"}
                  </span>
                  <select
                    value={form.type}
                    onChange={set("type")}
                    disabled={!!editingId}
                    className="mt-1 w-full border rounded-lg px-3 py-2 disabled:bg-gray-100 disabled:text-gray-500"
                  >
                    <option value="first_ride_free">First Ride Free</option>
                    <option value="waive_service_fee">Waive Zooma Charge</option>
                    <option value="rider_milestone_reward">Rider Reward</option>
                    <option value="custom_discount">Custom Discount</option>
                  </select>
                </label>
                <label className="text-sm">
                  <span className="text-gray-500">
                    Title <span className="text-red-500">*</span>
                  </span>
                  <input
                    value={form.title}
                    onChange={set("title")}
                    className="mt-1 w-full border rounded-lg px-3 py-2"
                    placeholder="First Ride Free"
                  />
                </label>
                <label className="text-sm sm:col-span-2">
                  <span className="text-gray-500">Description</span>
                  <input
                    value={form.description}
                    onChange={set("description")}
                    className="mt-1 w-full border rounded-lg px-3 py-2"
                    placeholder="Your first Zooma ride is on us!"
                  />
                </label>
                {form.type !== "custom_discount" && (
                  <label className="text-sm">
                    <span className="text-gray-500">
                      Budget (₦) <span className="text-red-500">*</span>
                    </span>
                    <input
                      type="number"
                      value={form.budgetNaira}
                      onChange={set("budgetNaira")}
                      className="mt-1 w-full border rounded-lg px-3 py-2"
                      placeholder="15000"
                    />
                  </label>
                )}
                {form.type === "rider_milestone_reward" && (
                  <>
                    <label className="text-sm">
                      <span className="text-gray-500">Reward (₦)</span>
                      <input
                        type="number"
                        value={form.rewardNaira}
                        onChange={set("rewardNaira")}
                        className="mt-1 w-full border rounded-lg px-3 py-2"
                        placeholder="200"
                      />
                    </label>
                    <label className="text-sm">
                      <span className="text-gray-500">Rides per reward</span>
                      <input
                        type="number"
                        value={form.milestoneRides}
                        onChange={set("milestoneRides")}
                        className="mt-1 w-full border rounded-lg px-3 py-2"
                        placeholder="5"
                      />
                    </label>
                  </>
                )}
                {form.type === "custom_discount" && (
                  <>
                    <label className="text-sm">
                      <span className="text-gray-500">Ride category</span>
                      <select
                        value={form.rideCategory}
                        onChange={set("rideCategory")}
                        className="mt-1 w-full border rounded-lg px-3 py-2"
                      >
                        <option value="both">Both (normal + charter)</option>
                        <option value="normal">Book a seat (normal)</option>
                        <option value="charter">Charter only</option>
                      </select>
                    </label>
                    <label className="text-sm">
                      <span className="text-gray-500">% off fare</span>
                      <input
                        type="number"
                        min="0"
                        max="100"
                        value={form.percentOffFare}
                        onChange={set("percentOffFare")}
                        className="mt-1 w-full border rounded-lg px-3 py-2"
                        placeholder="0"
                      />
                    </label>
                    <label className="text-sm">
                      <span className="text-gray-500">% off Zooma charge</span>
                      <input
                        type="number"
                        min="0"
                        max="100"
                        value={form.percentOffCharge}
                        onChange={set("percentOffCharge")}
                        className="mt-1 w-full border rounded-lg px-3 py-2"
                        placeholder="0"
                      />
                    </label>
                    <p className="text-xs text-gray-400 sm:col-span-2 -mt-2">
                      Set at least one of the two % values above.
                    </p>

                    {/* Exactly ONE limit: budget, people, or duration. */}
                    <label className="text-sm">
                      <span className="text-gray-500">
                        Limit by <span className="text-red-500">*</span>
                      </span>
                      <select
                        value={form.limitType}
                        onChange={set("limitType")}
                        className="mt-1 w-full border rounded-lg px-3 py-2"
                      >
                        <option value="budget">Budget (₦)</option>
                        <option value="people">Number of people</option>
                        <option value="duration">Duration (dates)</option>
                      </select>
                    </label>
                    {form.limitType === "budget" && (
                      <label className="text-sm">
                        <span className="text-gray-500">
                          Budget (₦) <span className="text-red-500">*</span>
                        </span>
                        <input
                          type="number"
                          min="0"
                          value={form.budgetNaira}
                          onChange={set("budgetNaira")}
                          className="mt-1 w-full border rounded-lg px-3 py-2"
                          placeholder="15000"
                        />
                      </label>
                    )}
                    {form.limitType === "people" && (
                      <label className="text-sm">
                        <span className="text-gray-500">
                          Number of people <span className="text-red-500">*</span>
                        </span>
                        <input
                          type="number"
                          min="1"
                          value={form.maxRedemptions}
                          onChange={set("maxRedemptions")}
                          className="mt-1 w-full border rounded-lg px-3 py-2"
                          placeholder="100"
                        />
                      </label>
                    )}
                    {form.limitType === "duration" && (
                      <>
                        <label className="text-sm">
                          <span className="text-gray-500">
                            Starts on <span className="text-red-500">*</span>
                          </span>
                          <input
                            type="date"
                            value={form.startsAt}
                            onChange={set("startsAt")}
                            className="mt-1 w-full border rounded-lg px-3 py-2"
                          />
                        </label>
                        <label className="text-sm">
                          <span className="text-gray-500">
                            Ends on <span className="text-red-500">*</span>
                          </span>
                          <input
                            type="date"
                            value={form.endsAt}
                            onChange={set("endsAt")}
                            className="mt-1 w-full border rounded-lg px-3 py-2"
                          />
                        </label>
                      </>
                    )}
                  </>
                )}
              </div>
              <div className="flex items-center gap-3 mt-4">
                <label className="flex items-center gap-2 text-sm text-gray-600">
                  <input
                    type="checkbox"
                    checked={form.active}
                    onChange={set("active")}
                  />
                  Activate immediately
                </label>
                <div className="flex-1" />
                <button
                  onClick={cancelForm}
                  disabled={saving}
                  className="px-4 py-2 rounded-lg border text-gray-600 text-sm font-medium disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  onClick={savePromo}
                  disabled={saving}
                  className="px-4 py-2 rounded-lg bg-green-600 text-white text-sm font-medium disabled:opacity-50"
                >
                  {saving
                    ? "Saving…"
                    : editingId
                      ? "Save changes"
                      : "Create"}
                </button>
              </div>
            </div>
          )}

          {/* List */}
          {loading ? (
            <div className="flex items-center justify-center py-20 text-gray-400">
              <Loader2 className="w-6 h-6 animate-spin" />
            </div>
          ) : promos.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-20 text-gray-400">
              <Gift className="w-10 h-10 mb-3" />
              <p>No promotions yet. Create one to get started.</p>
            </div>
          ) : (
            <div className="space-y-4 max-w-3xl">
              {promos.map((p) => {
                const cap = describeCap(p);
                return (
                  <div
                    key={p._id}
                    className="bg-white dark:bg-black rounded-xl shadow-sm p-5"
                  >
                    <div className="flex items-start justify-between gap-4 flex-wrap">
                      <div>
                        <div className="flex items-center gap-2">
                          <span className="font-semibold text-gray-900 dark:text-white">
                            {p.title}
                          </span>
                          <span className="text-[11px] px-2 py-0.5 rounded-full bg-gray-100 text-gray-600">
                            {TYPE_LABEL[p.type] || p.type}
                          </span>
                          <span className="text-[11px] px-2 py-0.5 rounded-full bg-gray-100 text-gray-600">
                            {p.audience}
                          </span>
                        </div>
                        {p.description && (
                          <p className="text-sm text-gray-500 mt-0.5">
                            {p.description}
                          </p>
                        )}
                      </div>

                      {canEdit ? (
                      <div className="flex items-center gap-3">
                        {/* Edit */}
                        <button
                          onClick={() => startEdit(p)}
                          className="text-sm font-medium text-gray-600 hover:text-black underline"
                        >
                          Edit
                        </button>

                        {/* Active toggle */}
                        <button
                          onClick={() => toggleActive(p)}
                          disabled={togglingId === p._id}
                          className={`relative w-12 h-6 rounded-full transition ${
                            p.active ? "bg-green-500" : "bg-gray-300"
                          } disabled:opacity-50`}
                          aria-label="Toggle active"
                        >
                          <span
                            className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full transition ${
                              p.active ? "translate-x-6" : ""
                            }`}
                          />
                        </button>
                      </div>
                      ) : (
                        <span
                          className={`text-xs px-2 py-0.5 rounded-full ${
                            p.active
                              ? "bg-green-100 text-green-800"
                              : "bg-gray-100 text-gray-600"
                          }`}
                        >
                          {p.active ? "Active" : "Inactive"}
                        </span>
                      )}
                    </div>

                    {/* Consumption bar — against whichever cap is actually
                        active, not always the budget. A promotion limited by
                        headcount or by date used to render an empty ₦0 budget
                        bar, which read as a dead campaign when it was live. */}
                    <div className="mt-4">
                      <div className="flex justify-between text-xs text-gray-500 mb-1">
                        <span>{cap.usedLabel}</span>
                        <span>{cap.leftLabel}</span>
                      </div>
                      <div className="w-full h-2 bg-gray-100 rounded-full overflow-hidden">
                        <div
                          className={`h-full ${
                            cap.pct >= 100
                              ? "bg-red-500"
                              : cap.pct >= 80
                                ? "bg-amber-500"
                                : "bg-green-500"
                          }`}
                          style={{ width: `${cap.pct}%` }}
                        />
                      </div>
                      <div className="flex justify-between items-center mt-2 gap-3">
                        <p className="text-xs text-gray-400">{cap.capLabel}</p>
                        {/* Spend is always worth showing, even when it is not
                            the thing capping the promotion. */}
                        {cap.kind !== "budget" && (
                          <p className="text-xs text-gray-400">
                            {naira(p.spentKobo)} spent
                          </p>
                        )}
                      </div>
                      {p.type === "rider_milestone_reward" && (
                        <p className="text-xs text-gray-400 mt-1">
                          {naira(p.rewardKobo)} every {p.milestoneRides} rides
                        </p>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </main>
      </div>
    </div>
  );
};

export default Promotions;
